import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import ArenaxLayout from './ArenaxLayout';
import { ArrowRight, ArrowLeft, MapPin, Clock, Users, Loader2, Trophy, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const SPORT_ICONS = {
  Football: '⚽', Cricket: '🏏', Badminton: '🏸', Tennis: '🎾', Basketball: '🏀',
  Hockey: '🏑', Volleyball: '🏐', Pickleball: '🏓', 'Table Tennis': '🏓',
  Squash: '🎾', Athletics: '🏃', Other: '🏟️',
};
const SKILLS = ['beginner','intermediate','advanced','pro'];

// 5-step wizard: Sport → Venue → Date+Slot → Skill+Players → Review+Create
export default function ArenaxHostGame() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [venues, setVenues] = useState([]);
  const [slots, setSlots] = useState([]);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    sport: '', venue: null, date: new Date().toISOString().slice(0, 10), slot: null,
    skill_level: 'intermediate', min_players: 4, max_players: 8,
    entry_fee_per_player: 200, needs_note: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/arenax/auth/login?next=/arenax/games/host');
    }
  }, [user, navigate]);

  // Load venues whenever sport changes
  useEffect(() => {
    if (!form.sport) return;
    api.get(`/arenax/venues?sport=${encodeURIComponent(form.sport)}`).then(r => setVenues(r.data.venues || [])).catch(() => {});
  }, [form.sport]);

  // Load slots when venue or date changes
  useEffect(() => {
    if (!form.venue || !form.date) return;
    api.get(`/arenax/venues/${form.venue.id}/availability?date=${form.date}`)
      .then(r => setSlots((r.data.slots || []).filter(s => s.status === 'available' && (form.sport ? s.sport === form.sport : true))))
      .catch(() => setSlots([]));
  }, [form.venue, form.date, form.sport]);

  const canAdvance = useMemo(() => {
    if (step === 1) return !!form.sport;
    if (step === 2) return !!form.venue;
    if (step === 3) return !!form.slot;
    if (step === 4) return form.min_players >= 2 && form.max_players >= form.min_players;
    return true;
  }, [step, form]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        venue_id: form.venue.id, slot_id: form.slot.id,
        sport: form.sport, skill_level: form.skill_level,
        min_players: Number(form.min_players), max_players: Number(form.max_players),
        entry_fee_per_player: Number(form.entry_fee_per_player) || 0,
        needs_note: form.needs_note || undefined,
      };
      const r = await api.post('/arenax/games', payload);
      toast.success('Game published! Inviting your network...');
      navigate(`/arenax/games/${r.data.game.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create game');
    } finally { setBusy(false); }
  };

  return (
    <ArenaxLayout>
      <section className="max-w-3xl mx-auto px-5 py-10" data-testid="arenax-host-game">
        <div className="mb-8">
          <div className="text-[11px] tracking-[0.4em] text-amber-300 font-bold mb-2">HOST A GAME · 30 SECONDS</div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-100">Start a game.</h1>
          <p className="text-sm text-slate-400 mt-1">Pick the sport, lock the venue, set the rules. Your city joins.</p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-2 mb-7">
          {[1, 2, 3, 4, 5].map(n => (
            <div key={n} className={`h-1.5 flex-1 rounded-full transition-all ${
              step === n ? 'bg-gradient-to-r from-cyan-400 to-amber-400' :
              step > n ? 'bg-cyan-500' : 'bg-slate-800'
            }`} />
          ))}
        </div>

        <div className="rounded-2xl border border-cyan-500/15 p-6 min-h-[400px] arenax-operational-form" style={{ background: 'linear-gradient(160deg, rgba(8,145,178,0.05), var(--ax-card-bg-soft))' }}>
          {step === 1 && <StepSport form={form} setForm={setForm} />}
          {step === 2 && <StepVenue venues={venues} form={form} setForm={setForm} />}
          {step === 3 && <StepSlot slots={slots} form={form} setForm={setForm} />}
          {step === 4 && <StepRules form={form} setForm={setForm} />}
          {step === 5 && <StepReview form={form} />}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-5">
          <button onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1}
            data-testid="arenax-host-back"
            className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-400 hover:text-slate-200 flex items-center gap-1 disabled:opacity-30">
            <ArrowLeft className="h-3.5 w-3.5" />Back
          </button>
          {step < 5 ? (
            <button onClick={() => setStep(s => s + 1)} disabled={!canAdvance} data-testid="arenax-host-next"
              className="px-6 py-3 rounded-xl text-sm font-bold text-slate-950 flex items-center gap-2 disabled:opacity-40"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              Next <ArrowRight className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={busy} data-testid="arenax-host-submit"
              className="px-6 py-3 rounded-xl text-sm font-bold text-slate-950 flex items-center gap-2 disabled:opacity-50"
              style={{ background: 'var(--ax-primary-gradient)' }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Publish Game <Sparkles className="h-4 w-4" /></>}
            </button>
          )}
        </div>
      </section>
    </ArenaxLayout>
  );
}

function StepSport({ form, setForm }) {
  const sports = ['Football','Cricket','Badminton','Tennis','Basketball','Pickleball','Volleyball','Table Tennis'];
  return (
    <div>
      <div className="text-[11px] tracking-[0.3em] text-cyan-300 font-bold mb-3">STEP 1 · SPORT</div>
      <h2 className="text-xl font-bold text-slate-100 mb-5">What are you playing?</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {sports.map(s => (
          <button key={s} onClick={() => setForm({ ...form, sport: s })}
            data-testid={`arenax-host-sport-${s.toLowerCase().replace(/ /g, '-')}`}
            className={`aspect-square rounded-2xl border flex flex-col items-center justify-center gap-1.5 transition-all ${
              form.sport === s
                ? 'bg-cyan-500/20 border-cyan-500 ring-2 ring-cyan-500/40'
                : 'bg-slate-900/40 border-slate-800 hover:border-cyan-500/40'
            }`}>
            <span className="text-3xl">{SPORT_ICONS[s] || '🏟️'}</span>
            <span className="text-xs font-bold text-slate-200">{s}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function StepVenue({ venues, form, setForm }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.3em] text-cyan-300 font-bold mb-3">STEP 2 · VENUE</div>
      <h2 className="text-xl font-bold text-slate-100 mb-5">Where will you play?</h2>
      {venues.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-500">No venues support {form.sport} yet. Try another sport.</div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {venues.map(v => (
            <button key={v.id} onClick={() => setForm({ ...form, venue: v })}
              data-testid={`arenax-host-venue-${v.id}`}
              className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 ${
                form.venue?.id === v.id
                  ? 'bg-cyan-500/15 border-cyan-500 ring-2 ring-cyan-500/30'
                  : 'bg-slate-900/40 border-slate-800 hover:border-cyan-500/40'
              }`}>
              <div className="h-12 w-12 rounded-lg shrink-0" style={{
                background: v.cover_image_url ? `url(${v.cover_image_url}) center/cover` : 'linear-gradient(135deg, #0e7490, #f59e0b)',
              }} />
              <div className="flex-1">
                <div className="text-sm font-bold text-slate-100">{v.name}</div>
                <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{v.city || 'India'}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StepSlot({ slots, form, setForm }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.3em] text-cyan-300 font-bold mb-3">STEP 3 · DATE & TIME</div>
      <h2 className="text-xl font-bold text-slate-100 mb-4">When?</h2>
      <div className="mb-4">
        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1.5 block">Date</label>
        <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value, slot: null })}
          data-testid="arenax-host-date"
          min={new Date().toISOString().slice(0, 10)}
          className="arenax-input w-full sm:w-64 px-3 py-2.5 rounded-xl text-sm" />
      </div>
      {slots.length === 0 ? (
        <div className="text-center py-8 text-sm text-slate-500">No available slots for this venue/date. Try another date.</div>
      ) : (
        <>
          <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2 block">Pick a slot</label>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-64 overflow-y-auto pr-1">
            {slots.map(s => (
              <button key={s.id} onClick={() => setForm({ ...form, slot: s })}
                data-testid={`arenax-host-slot-${s.id}`}
                className={`px-2 py-2.5 rounded-lg text-[11px] font-bold transition-all text-left ${
                  form.slot?.id === s.id
                    ? 'bg-cyan-500 text-slate-950 ring-2 ring-cyan-300'
                    : 'bg-slate-900/60 text-slate-200 hover:bg-slate-800 border border-slate-700 hover:border-cyan-500/50'
                }`}>
                <div className="flex items-center gap-1"><Clock className="h-2.5 w-2.5 opacity-70" />{s.start_time}</div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="opacity-70">{s.end_time}</span>
                  {s.is_peak && <span className="text-amber-400">★</span>}
                </div>
                <div className="text-[10px] mt-0.5 opacity-80">₹{s.price}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StepRules({ form, setForm }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.3em] text-cyan-300 font-bold mb-3">STEP 4 · RULES</div>
      <h2 className="text-xl font-bold text-slate-100 mb-5">Set the rules.</h2>

      <div className="mb-5">
        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-2 block">Skill level</label>
        <div className="flex flex-wrap gap-2">
          {SKILLS.map(sk => (
            <button key={sk} onClick={() => setForm({ ...form, skill_level: sk })}
              data-testid={`arenax-host-skill-${sk}`}
              className={`px-4 py-2 rounded-full text-xs font-bold capitalize transition-all ${
                form.skill_level === sk ? 'bg-amber-500 text-slate-950 shadow-md' : 'bg-slate-900 text-slate-300 border border-slate-700 hover:border-amber-500/50'
              }`}>{sk}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1.5 block">Min players</label>
          <input type="number" min={2} max={form.max_players} value={form.min_players}
            data-testid="arenax-host-min-players"
            onChange={e => setForm({ ...form, min_players: Number(e.target.value) })}
            className="arenax-input w-full px-3 py-2.5 rounded-xl text-sm" />
          <div className="text-[10px] text-slate-600 mt-1">Game confirms when this many join</div>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1.5 block">Max players</label>
          <input type="number" min={form.min_players} value={form.max_players}
            data-testid="arenax-host-max-players"
            onChange={e => setForm({ ...form, max_players: Number(e.target.value) })}
            className="arenax-input w-full px-3 py-2.5 rounded-xl text-sm" />
          <div className="text-[10px] text-slate-600 mt-1">Hard cap for the squad</div>
        </div>
      </div>

      <div className="mb-5">
        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1.5 block">Entry fee per player (₹)</label>
        <input type="number" min={0} value={form.entry_fee_per_player}
          data-testid="arenax-host-fee"
          onChange={e => setForm({ ...form, entry_fee_per_player: Number(e.target.value) })}
          className="arenax-input w-full px-3 py-2.5 rounded-xl text-sm" />
        <div className="text-[10px] text-slate-600 mt-1">You pay the venue slot; joiners split via entry fees. Set to 0 to make it free.</div>
      </div>

      <div>
        <label className="text-[11px] uppercase tracking-wider text-slate-400 font-bold mb-1.5 block">Squad need (optional)</label>
        <input type="text" value={form.needs_note}
          data-testid="arenax-host-note"
          onChange={e => setForm({ ...form, needs_note: e.target.value })}
          placeholder="e.g. Need 2 defenders and a goalkeeper"
          maxLength={120}
          className="arenax-input w-full px-3 py-2.5 rounded-xl text-sm" />
      </div>
    </div>
  );
}

function StepReview({ form }) {
  const slotCost = form.slot?.price || 0;
  return (
    <div data-testid="arenax-host-review">
      <div className="text-[11px] tracking-[0.3em] text-cyan-300 font-bold mb-3">STEP 5 · REVIEW</div>
      <h2 className="text-xl font-bold text-slate-100 mb-5">Looks good?</h2>
      <div className="space-y-3">
        <ReviewRow label="Sport" value={`${SPORT_ICONS[form.sport] || '🏟️'} ${form.sport}`} />
        <ReviewRow label="Venue" value={`${form.venue?.name} · ${form.venue?.city}`} />
        <ReviewRow label="Date · Time" value={`${form.date} · ${form.slot?.start_time}–${form.slot?.end_time}`} />
        <ReviewRow label="Skill" value={form.skill_level} />
        <ReviewRow label="Players" value={`Min ${form.min_players} · Max ${form.max_players}`} />
        <ReviewRow label="Entry fee" value={form.entry_fee_per_player > 0 ? `₹${form.entry_fee_per_player} per player` : 'Free'} />
        {form.needs_note && <ReviewRow label="Note" value={form.needs_note} />}
        <div className="rounded-xl bg-cyan-500/10 border border-cyan-500/30 p-4 mt-4">
          <div className="text-[10px] tracking-[0.2em] uppercase text-cyan-300 font-bold mb-1">YOU PAY THE VENUE NOW</div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-300">Slot cost (mock-pay until live mode)</span>
            <span className="text-xl font-black text-cyan-300">₹{slotCost}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-slate-900/40 border border-slate-800">
      <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{label}</span>
      <span className="text-sm font-semibold text-slate-100 text-right max-w-[60%] capitalize">{value}</span>
    </div>
  );
}
